import { Notification } from "@/utils/notificationUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clientApi } from "../../../modules/client/api";
import { CreateUpdateClientAgreement } from "../../../modules/client/types";
import { EntityObject, PageRequest, TwoLevelEntityIdObject, TwoLevelEntityObject } from "../../types";

export const CLIENT_AGREEMENTS_QUERY_KEYS = {
  filterClientAgreements: (payload?: EntityObject<PageRequest>) =>
    payload ? ["filterClientAgreements", payload] : ["filterClientAgreements"],
  createClientAgreement: () => ["createClientAgreement"],
  updateClientAgreement: () => ["updateClientAgreement"],
  deleteClientAgreement: () => ["deleteClientAgreement"],
};

export const useFilterClientAgreements = (payload: EntityObject<PageRequest>) => {
  return useQuery({
    queryKey: CLIENT_AGREEMENTS_QUERY_KEYS.filterClientAgreements(),
    queryFn: async () => {
      const { data } = await clientApi.filterClientAgreements(payload);

      return data;
    },
  });
};

export const useCreateClientAgreement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CLIENT_AGREEMENTS_QUERY_KEYS.createClientAgreement(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateClientAgreement>) => {
      const { data } = await clientApi.createClientAgreement(payload);

      return data;
    },
    onSuccess: () => {
      Notification.itemCreatedNotification();
      queryClient.invalidateQueries({ queryKey: CLIENT_AGREEMENTS_QUERY_KEYS.filterClientAgreements() });
    },
  });
};

export const useUpdateClientAgreement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CLIENT_AGREEMENTS_QUERY_KEYS.updateClientAgreement(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<CreateUpdateClientAgreement>) => {
      const { data } = await clientApi.updateClientAgreement(payload);

      return data;
    },
    onSuccess: () => {
      Notification.itemUpdatedNotification();
      queryClient.invalidateQueries({ queryKey: CLIENT_AGREEMENTS_QUERY_KEYS.filterClientAgreements() });
    },
  });
};

export const useDeleteClientAgreement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CLIENT_AGREEMENTS_QUERY_KEYS.deleteClientAgreement(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return clientApi.deleteClientAgreement(payload);
    },
    onSuccess: () => {
      Notification.itemDeletedNotification();
      queryClient.invalidateQueries({ queryKey: CLIENT_AGREEMENTS_QUERY_KEYS.filterClientAgreements() });
    },
  });
};
